import { firstValueFrom, Observable } from 'rxjs';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export class RequestParams {
  private _url = '';
  private _payload: unknown = undefined;
  private _queryParams: Record<string, unknown> = {};
  private _authenticated = false;
  private _filePayload: File | FormData | undefined = undefined;
  private _customHeaders: Record<string, string> = {};
  private _responseType: 'json' | 'blob' = 'json';
  private _observe: 'body' | 'response' = 'body';

  constructor(
    readonly method: HttpMethod,
    private readonly _executor: (params: RequestParams) => Observable<unknown>
  ) {}

  url(template: string, ...args: (string | number)[]): this {
    let i = 0;
    this._url = template.replaceAll('{}', () => encodeURIComponent(String(args[i++])));
    return this;
  }

  payload(data: unknown): this {
    this._payload = data;
    return this;
  }

  params(data: Record<string, unknown>): this {
    this._queryParams = { ...this._queryParams, ...data };
    return this;
  }

  filter(data: Record<string, unknown>): this {
    const clean = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v != null)
    );
    return this.params({ filter: JSON.stringify(clean) });
  }

  sort(field: string, desc = false): this {
    return this.params({ sort: desc ? `-${field}` : field });
  }

  page(n: number): this {
    return this.params({ page: n });
  }

  per_page(n: number): this {
    return this.params({ per_page: n });
  }

  all(): this {
    return this.params({ o_all: true });
  }

  auth(): this {
    this._authenticated = true;
    return this;
  }

  file(f: File | FormData): this {
    this._filePayload = f;
    return this;
  }

  headers(h: Record<string, string>): this {
    this._customHeaders = { ...this._customHeaders, ...h };
    return this;
  }

  responseType(type: 'json' | 'blob'): this {
    this._responseType = type;
    return this;
  }

  observeResponse(): this {
    this._observe = 'response';
    return this;
  }

  promise<T = any>(): Promise<T> {
    return firstValueFrom(this._executor(this) as Observable<T>);
  }

  observable<T = any>(): Observable<T> {
    return this._executor(this) as Observable<T>;
  }

  // --- internal getters used by RequestService.execute() ---

  get resolvedUrl(): string { return this._url; }
  get requestPayload(): unknown { return this._payload; }
  get filePayload(): File | FormData | undefined { return this._filePayload; }
  get queryParams(): Record<string, unknown> { return this._queryParams; }
  get authenticated(): boolean { return this._authenticated; }
  get customHeaders(): Record<string, string> { return this._customHeaders; }
  get responseTypeValue(): 'json' | 'blob' { return this._responseType; }
  get observeValue(): 'body' | 'response' { return this._observe; }
}
